import React from "react";
import { Edit, Trash2, Users, Mail, MessageSquare, Loader2 } from "lucide-react";

const CHANNEL_LABELS = {
  email: "Email",
  sms: "SMS",
  both: "Email & SMS",
};

const MarketingCampaignItemCard = ({
  campaign,
  onView,
  onEdit,
  onDelete,
  onSendEmail,
  onSendSms,
  isSendingEmail = false,
  isSendingSms = false,
}) => {
  const candidateCount =
    campaign.candidates_count ??
    campaign.candidate_count ??
    (Array.isArray(campaign.candidate_ids)
      ? campaign.candidate_ids.length
      : Array.isArray(campaign.candidates)
        ? campaign.candidates.length
        : 0);

  const templateName =
    campaign.template_name ||
    campaign.template?.name ||
    campaign.template_title ||
    "-";

  const channel =
    campaign.channel ||
    campaign.template?.channel ||
    campaign.template_channel ||
    "email";

  const showEmailSend = channel === "email" || channel === "both";
  const showSmsSend = channel === "sms" || channel === "both";
  const isBusy = isSendingEmail || isSendingSms;

  return (
    <div
      onClick={() => onView?.(campaign)}
      className="flex flex-col bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-all relative overflow-hidden min-h-[200px] cursor-pointer"
    >
      {(showEmailSend || showSmsSend) && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
          {showSmsSend && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!isBusy) onSendSms?.(campaign);
              }}
              disabled={isBusy}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send SMS"
            >
              {isSendingSms ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MessageSquare className="w-4 h-4" />
              )}
            </button>
          )}
          {showEmailSend && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!isBusy) onSendEmail?.(campaign);
              }}
              disabled={isBusy}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send Email"
            >
              {isSendingEmail ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      )}

      <div className="self-stretch p-5 flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex justify-start items-center gap-3 min-w-0 pr-10">
          <h2
            className="flex-1 min-w-0 text-foreground text-base font-semibold leading-6 truncate"
            title={campaign.name}
          >
            {campaign.name}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {channel && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground">
              {CHANNEL_LABELS[channel] || channel}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-blue-500/10 text-blue-500">
            <Users className="w-3.5 h-3.5" />
            {candidateCount} candidate{candidateCount === 1 ? "" : "s"}
          </span>
        </div>

        <div className="self-stretch">
          <p className="text-xs font-medium text-muted-foreground mb-1">Template</p>
          <p
            className="text-sm font-medium text-foreground truncate"
            title={templateName}
          >
            {templateName}
          </p>
        </div>
      </div>

      <div className="mt-auto self-stretch flex flex-col justify-start items-center">
        <div className="self-stretch h-px bg-border" />
        <div className="self-stretch px-5 py-4 inline-flex justify-between items-center gap-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(campaign);
            }}
            className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 text-sm font-semibold"
            title="Edit Campaign"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(campaign);
            }}
            className="text-muted-foreground hover:text-red-600 transition-colors"
            title="Delete Campaign"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingCampaignItemCard;
